import { type CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';
import {
	fireOperationalEvent,
	fireScreenEvent,
	fireTrackEvent,
	fireUIEvent,
} from '@atlassian/teams-common/analytics';

import { type TeamMembershipSettings } from '../../types/team';
import { ContainerType } from '../CreateContainer/types';
import { type AnalyticsEventAttributes } from '../TeamCreateDialog/types';

import { teamCreationExp } from './experiences';

export enum AnalyticsActionSubject {
	ERROR_BOUNDARY = 'errorBoundary',
	MEMBER_PICKER = 'memberPicker',
	TEAM_TYPE_PICKER = 'teamTypePicker',
	TEAM_CREATE_DIALOG = 'teamCreateDialog',
	DROP_DOWN_MENU = 'dropdownMenu',
	ADD_TO_TEAM_SERVICE = 'addToTeamService',
	INVITE_TO_PRODUCT_SERVICE = 'inviteToProductService',
	INVITE_CAPABILITIES_SERVICE = 'inviteCapabilitiesService',
	INVITED_TEAM_MEMBERS = 'invitedTeamMembers',
	USER = 'user',
	REQUESTED_CONTAINERS = 'requestedContainers',
	CONTAINER_PERMISSIONS = 'containerPermissions',
	MEMBER = 'member',
}

export enum AnalyticsAction {
	RENDERED = 'rendered',
	CLICKED = 'clicked',
	FAILED = 'failed',
	SUCCEEDED = 'succeeded',
	VIEWED = 'viewed',
	CLOSED = 'closed',
	ERROR = 'error',
	SUBMITED = 'submitted',
	FETCHED = 'fetched',
	SENT = 'sent',
	INVITED = 'invited',
	REQUESTED = 'requested',
	ADDED = 'added',
	SUGGESTED = 'suggested',
}

export enum AnalyticsSource {
	PEOPLE_TEAMS = 'peopleTeams',
}

const source = AnalyticsActionSubject.TEAM_CREATE_DIALOG;

export const triggerAnalyticsForMemberPickerError = (
	createAnalyticsEvent?: CreateUIAnalyticsEvent,
	extraAnalyticsAttrs: AnalyticsEventAttributes = {},
) => {
	fireTrackEvent(createAnalyticsEvent, {
		action: AnalyticsAction.ERROR,
		actionSubject: AnalyticsActionSubject.MEMBER_PICKER,
		attributes: {
			...extraAnalyticsAttrs,
		},
		source,
	});
};

export const triggerAnalyticsForViewTeamCreateDialog = (
	createAnalyticsEvent?: CreateUIAnalyticsEvent,
	proposedMembersLength?: number,
	extraAnalyticsAttrs: AnalyticsEventAttributes = {},
) => {
	fireScreenEvent(createAnalyticsEvent, {
		attributes: {
			proposedMembersLength,
			...extraAnalyticsAttrs,
		},
		name: 'teamCreateDialog',
		source,
	});
};

export const triggerAnalyticsForRequestedContainers = (
	createAnalyticsEvent?: CreateUIAnalyticsEvent,
	containers?: ContainerType[],
	extraAnalyticsAttrs: AnalyticsEventAttributes = {},
) => {
	fireTrackEvent(createAnalyticsEvent, {
		action: AnalyticsAction.REQUESTED,
		actionSubject: AnalyticsActionSubject.REQUESTED_CONTAINERS,
		attributes: {
			containers: Object.values(ContainerType).reduce(
				(acc, type) => {
					acc[type] = containers?.includes(type) ?? false;
					return acc;
				},
				{} as Record<string, boolean>,
			),
			...extraAnalyticsAttrs,
		},
		source,
	});
};

export const triggerAnalyticsForCloseTeamCreateDialog = (
	createAnalyticsEvent?: CreateUIAnalyticsEvent,
	extraAnalyticsAttrs: AnalyticsEventAttributes = {},
) => {
	fireUIEvent(createAnalyticsEvent, {
		action: AnalyticsAction.CLOSED,
		actionSubject: AnalyticsActionSubject.TEAM_CREATE_DIALOG,
		attributes: {
			...extraAnalyticsAttrs,
		},
		source,
	});
};

export const triggerAnalyticsForSuggestedMemberSelection = (
	createAnalyticsEvent?: CreateUIAnalyticsEvent,
	extraAnalyticsAttrs: AnalyticsEventAttributes = {},
) => {
	fireUIEvent(createAnalyticsEvent, {
		action: AnalyticsAction.SUGGESTED,
		actionSubject: AnalyticsActionSubject.MEMBER,
		attributes: {
			...extraAnalyticsAttrs,
		},
		source,
	});
};

export const triggerAnalyticsForSubmitTeamCreateDialog = (
	createAnalyticsEvent?: CreateUIAnalyticsEvent,
	extraAnalyticsAttrs: AnalyticsEventAttributes = {},
) => {
	fireUIEvent(createAnalyticsEvent, {
		action: AnalyticsAction.SUBMITED,
		actionSubject: AnalyticsActionSubject.TEAM_CREATE_DIALOG,
		attributes: {
			...extraAnalyticsAttrs,
		},
		source,
	});

	teamCreationExp.start();
};

export const triggerAnalyticsForCreateTeamFailed = (
	createAnalyticsEvent?: CreateUIAnalyticsEvent,
	numberOfMembers?: number,
	extraAnalyticsAttrs: AnalyticsEventAttributes = {},
) => {
	fireOperationalEvent(createAnalyticsEvent, {
		action: AnalyticsAction.FAILED,
		actionSubject: AnalyticsActionSubject.TEAM_CREATE_DIALOG,
		attributes: {
			numberOfMembers,
			...extraAnalyticsAttrs,
		},
		source,
	});

	teamCreationExp.failure({
		metadata: {
			numberOfMembers,
			...extraAnalyticsAttrs,
		},
	});

	fireTrackEvent(createAnalyticsEvent, {
		action: AnalyticsAction.FAILED,
		actionSubject: AnalyticsActionSubject.TEAM_CREATE_DIALOG,
		attributes: {
			numberOfMembers,
			...extraAnalyticsAttrs,
		},
		source,
	});
};

export const triggerAnalyticsForCreateTeamSuccess = (
	createAnalyticsEvent?: CreateUIAnalyticsEvent,
	numberOfMembers?: number,
	extraAnalyticsAttrs: AnalyticsEventAttributes = {},
) => {
	fireOperationalEvent(createAnalyticsEvent, {
		action: AnalyticsAction.SUCCEEDED,
		actionSubject: AnalyticsActionSubject.TEAM_CREATE_DIALOG,
		attributes: {
			numberOfMembers,
			...extraAnalyticsAttrs,
		},
		source,
	});

	teamCreationExp.success({
		metadata: {
			numberOfMembers,
			...extraAnalyticsAttrs,
		},
	});

	fireTrackEvent(createAnalyticsEvent, {
		action: AnalyticsAction.SUCCEEDED,
		actionSubject: AnalyticsActionSubject.TEAM_CREATE_DIALOG,
		attributes: {
			numberOfMembers,
			...extraAnalyticsAttrs,
		},
		source,
	});
};

export const triggerAnalyticsForClickTeamLinkInSuccessFlag = (
	createAnalyticsEvent?: CreateUIAnalyticsEvent,
	teamId?: string,
	numberOfMembers?: number,
	extraAnalyticsAttrs: AnalyticsEventAttributes = {},
) => {
	fireUIEvent(createAnalyticsEvent, {
		action: AnalyticsAction.CLICKED,
		actionSubject: AnalyticsActionSubject.TEAM_CREATE_DIALOG,
		actionSubjectId: 'teamLinkSuccessFlag',
		attributes: {
			teamId,
			numberOfMembers,
			...extraAnalyticsAttrs,
		},
		source,
	});
};

export const triggerAnalyticsForTeamTypePickerInteraction = (
	createAnalyticsEvent?: CreateUIAnalyticsEvent,
	defaultType?: TeamMembershipSettings,
	chosenType?: TeamMembershipSettings,
) => {
	fireUIEvent(createAnalyticsEvent, {
		action: AnalyticsAction.CLICKED,
		actionSubject: AnalyticsActionSubject.TEAM_TYPE_PICKER,
		attributes: {
			defaultType,
			chosenType,
		},
		source,
	});
};

export const triggerAnalyticsForInviteCapabilitiesFailed = ({
	createAnalyticsEvent,
	extraAnalyticsAttrs = {},
	source = AnalyticsActionSubject.TEAM_CREATE_DIALOG,
}: {
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	extraAnalyticsAttrs: AnalyticsEventAttributes;
	source?: string;
}) => {
	fireTrackEvent(createAnalyticsEvent, {
		action: AnalyticsAction.FAILED,
		actionSubject: AnalyticsActionSubject.INVITE_CAPABILITIES_SERVICE,
		attributes: {
			...extraAnalyticsAttrs,
			integration: source,
		},
		source,
	});
};

export const triggerAnalyticsForAddToTeamFailed = ({
	createAnalyticsEvent,
	extraAnalyticsAttrs = {},
	source = AnalyticsActionSubject.TEAM_CREATE_DIALOG,
}: {
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	extraAnalyticsAttrs: AnalyticsEventAttributes;
	source?: string;
}) => {
	fireTrackEvent(createAnalyticsEvent, {
		action: AnalyticsAction.FAILED,
		actionSubject: AnalyticsActionSubject.ADD_TO_TEAM_SERVICE,
		attributes: {
			...extraAnalyticsAttrs,
			integration: source,
		},
		source,
	});
};

export const triggerAnalyticsForAddToTeam = ({
	createAnalyticsEvent,
	numberOfMembers,
	extraAnalyticsAttrs = {},
	source = AnalyticsActionSubject.TEAM_CREATE_DIALOG,
}: {
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	numberOfMembers: number;
	extraAnalyticsAttrs: AnalyticsEventAttributes;
	source?: string;
}) => {
	fireTrackEvent(createAnalyticsEvent, {
		action: AnalyticsAction.ADDED,
		actionSubject: AnalyticsActionSubject.INVITED_TEAM_MEMBERS,
		attributes: {
			numberOfMembers,
			...extraAnalyticsAttrs,
			integration: source,
		},
		source,
	});
};

export const triggerAnalyticsForInviteToProductFailed = ({
	createAnalyticsEvent,
	extraAnalyticsAttrs = {},
	source = AnalyticsActionSubject.TEAM_CREATE_DIALOG,
}: {
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	extraAnalyticsAttrs: AnalyticsEventAttributes;
	source?: string;
}) => {
	fireTrackEvent(createAnalyticsEvent, {
		action: AnalyticsAction.FAILED,
		actionSubject: AnalyticsActionSubject.INVITE_TO_PRODUCT_SERVICE,
		attributes: {
			...extraAnalyticsAttrs,
			integration: source,
		},
		source,
	});
};

export const triggerAnalyticsForInvite = ({
	userId,
	createAnalyticsEvent,
	extraAnalyticsAttrs = {},
	source = AnalyticsActionSubject.TEAM_CREATE_DIALOG,
}: {
	userId: string;
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	extraAnalyticsAttrs: AnalyticsEventAttributes;
	source?: string;
}) => {
	fireTrackEvent(createAnalyticsEvent, {
		action: AnalyticsAction.INVITED,
		actionSubject: AnalyticsActionSubject.USER,
		actionSubjectId: userId,
		attributes: {
			...extraAnalyticsAttrs,
			integration: source,
		},
		source,
	});
};

export const triggerAnalyticsForContainerPermissions = (
	createAnalyticsEvent?: CreateUIAnalyticsEvent,
	extraAnalyticsAttrs: AnalyticsEventAttributes = {},
) => {
	fireTrackEvent(createAnalyticsEvent, {
		action: AnalyticsAction.SUCCEEDED,
		actionSubject: AnalyticsActionSubject.CONTAINER_PERMISSIONS,
		attributes: {
			...extraAnalyticsAttrs,
		},
		source,
	});
};
